import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaFileExcel, FaSpinner, FaTable } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../../libs/axios';
import { SearchableSelect, SelectWithSearch, type SelectOption } from '../../../utils/select-with-search';
import { dateRangeError, dateRangeInputBounds } from '../../../utils/date-range.utils';

/**
 * Survey Workflow — Summary
 * =========================
 *
 * How many sites sit at each workflow stage, per project: one row per stage, an
 * "All" column holding every project's count for that stage, then one column
 * per project. Columns come from the API, so a new project needs no change
 * here.
 *
 * Counts are by the site's CURRENT workflow status, so a site lands in exactly
 * one row and "All" is the sum of that row's project columns.
 *
 * This lives in its own component because it is mounted in two places — the
 * Survey Workflow Report's Summary toggle and Project Sites' Summary toggle —
 * and the two have to be the same screen: same filters, same query, same
 * numbers. Anything Project Sites needed of its own would be a second
 * implementation drifting from this one, which is exactly what it replaced.
 *
 * Everything it needs it loads itself (project / department / activity / role
 * dropdowns, and the engineer list scoped to the picked projects), so a host
 * page only has to render it.
 */

interface SummaryProjectColumn {
  id: number;
  project_name: string;
  project_short_name: string | null;
}

interface SummaryStageRow {
  stage: string;
  stage_label: string;
  sequence: number;
  all: number;
  counts: Record<string, number>;
}

interface SummaryPayload {
  projects: SummaryProjectColumn[];
  rows: SummaryStageRow[];
  totals: { all: number; counts: Record<string, number> };
}

interface SummaryFilters {
  stage_date_from: string;
  stage_date_to: string;
  /** CSV of status buckets, or 'all' — see COMPLETION_STATUS_OPTIONS. */
  completion_status: string;
  site_id: string;
  search: string;
}

const DEFAULT_SUMMARY_FILTERS: SummaryFilters = {
  stage_date_from: '',
  stage_date_to: '',
  completion_status: 'in_progress,completed',
  site_id: '',
  search: '',
};

/* Work Completion Status — a multi-select over `status` on the site.
 *
 * `status` carries twelve values, but nine of them are waypoints on the way
 * through the workflow (engineer assigned, TA done, report done, …). Users
 * think in four buckets, so the filter offers four and the server folds the
 * waypoints into "In Progress". The buckets are therefore exhaustive: every
 * site matches exactly one, so "All" really is the sum of the four and nothing
 * can hide between the options.
 *
 * The value travels as a CSV string ('in_progress,completed') so the whole
 * filter object stays plain strings; 'all' is the no-filter answer. The default
 * is In Progress + Complete, which is the old "Without Cancelled & Hold Sites"
 * behaviour expressed in the new buckets.
 */
const COMPLETION_STATUS_ALL = 'all';

const COMPLETION_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'hold', label: 'Hold' },
];

const parseCompletionStatus = (value: string): string[] =>
  String(value || '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

/** True when the selection means "every status" — explicitly, or by covering them all. */
const isAllCompletionStatuses = (value: string): boolean => {
  const tokens = parseCompletionStatus(value);
  if (tokens.length === 0 || tokens.includes(COMPLETION_STATUS_ALL)) return true;
  return COMPLETION_STATUS_OPTIONS.every((option) => tokens.includes(option.value));
};

/** Label for the closed dropdown. */
const completionStatusSummary = (value: string): string => {
  if (isAllCompletionStatuses(value)) return 'All';
  const tokens = parseCompletionStatus(value);
  const picked = COMPLETION_STATUS_OPTIONS.filter((option) => tokens.includes(option.value));
  return picked.length > 0 ? picked.map((option) => option.label).join(', ') : 'All';
};

/**
 * The Work Completion Status dropdown: a checkbox list rather than a `<select>`,
 * because several statuses can be reported on at once.
 *
 * "All" and the individual buckets are mutually exclusive on screen — ticking a
 * bucket while All is on narrows to just that bucket, and clearing the last
 * bucket falls back to All, so the filter is never sent empty.
 */
const CompletionStatusFilter: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const tokens = parseCompletionStatus(value);
  const allSelected = isAllCompletionStatuses(value);

  const toggle = (option: string) => {
    const next = allSelected
      ? [option]
      : tokens.includes(option)
        ? tokens.filter((token) => token !== option)
        : [...tokens.filter((token) => token !== COMPLETION_STATUS_ALL), option];
    // Every bucket — or none at all — is just "All".
    if (next.length === 0 || next.length === COMPLETION_STATUS_OPTIONS.length) {
      onChange(COMPLETION_STATUS_ALL);
      return;
    }
    onChange(next.join(','));
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-600"
      >
        <span className="truncate text-gray-900">{completionStatusSummary(value)}</span>
        <FaChevronDown
          className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg py-1">
          <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-blue-50 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onChange(COMPLETION_STATUS_ALL)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="font-medium">All</span>
          </label>
          <div className="border-t border-gray-100 my-1" />
          {COMPLETION_STATUS_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-blue-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={!allSelected && tokens.includes(option.value)}
                onChange={() => toggle(option.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const SurveyWorkflowSummary: React.FC = () => {
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>([]);
  const [activityOptions, setActivityOptions] = useState<SelectOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);

  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedRole, setSelectedRole] = useState<SelectOption | null>(null);
  const [filters, setFilters] = useState<SummaryFilters>(DEFAULT_SUMMARY_FILTERS);

  const [summary, setSummary] = useState<SummaryPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // The dropdowns are all scoped server-side and are loaded INDEPENDENTLY: one
  // failing endpoint must not leave the whole form empty, and the toast names
  // the source that failed.
  useEffect(() => {
    const loadFrom = async <T,>(
      path: string,
      label: string,
      pick: (data: any) => T,
      apply: (value: T) => void,
    ) => {
      try {
        const response = await axiosInstance.get(path);
        if (!response.data?.success) throw new Error(response.data?.message || label);
        apply(pick(response.data.data || {}));
      } catch (error: any) {
        console.error(`[survey-workflow-summary] ${path} failed:`, error);
        toast.error(
          error?.response?.status === 404
            ? `${label} unavailable — the server is running an older build.`
            : error?.response?.data?.message || `Failed to load ${label.toLowerCase()}`,
          { id: `swf-summary-filter-${path}` },
        );
      }
    };

    loadFrom(
      '/reports/survey-workflow/projects',
      'Projects',
      (data) =>
        (data.projects || []).map((project: any) => ({
          id: project.id,
          value: project.id,
          label: project.project_short_name
            ? `${project.project_name} (${project.project_short_name})`
            : project.project_name,
        })) as SelectOption[],
      setProjectOptions,
    );
    loadFrom(
      '/reports/survey-workflow/departments',
      'Departments',
      (data) =>
        (data.departments || []).map((department: any) => ({
          id: department.id,
          value: department.id,
          label: department.name,
        })) as SelectOption[],
      setDepartmentOptions,
    );
    loadFrom(
      '/reports/survey-workflow/activities',
      'Activities',
      (data) =>
        (data.activities || []).map((activity: any) => ({
          id: activity.id,
          value: activity.id,
          label: activity.activity_name,
        })) as SelectOption[],
      setActivityOptions,
    );
    loadFrom(
      '/reports/survey-workflow/roles',
      'Roles',
      (data) =>
        (data.roles || []).map((role: any) => ({
          id: role.id,
          value: role.id,
          label: role.name,
        })) as SelectOption[],
      setRoleOptions,
    );
  }, []);

  // Users are always fetched within the caller's own projects; picking
  // projects narrows them further to those projects' members. `projectIds` is
  // a CSV list, so one project and several are the same call.
  const loadUsers = useCallback(async (projectIds: string): Promise<SelectOption[]> => {
    try {
      const response = await axiosInstance.get('/reports/survey-workflow/users', {
        params: projectIds ? { project_id: projectIds } : undefined,
      });
      if (!response.data?.success) return [];
      return (response.data.data?.users || []).map((user: any) => ({
        id: user.id,
        value: user.id,
        label: [
          user.name || user.username,
          user.employee_code ? `(${user.employee_code})` : null,
          user.designation ? `- ${user.designation}` : null,
        ]
          .filter(Boolean)
          .join(' '),
      }));
    } catch (error: any) {
      console.error('[survey-workflow-summary] /reports/survey-workflow/users failed:', error);
      toast.error(
        error?.response?.status === 404
          ? 'User list unavailable — the server is running an older build.'
          : error?.response?.data?.message || 'Failed to load users',
        { id: 'swf-summary-filter-users' },
      );
      return [];
    }
  }, []);

  const projectScope = selectedProjects.map((option) => option.value).join(',');

  useEffect(() => {
    let cancelled = false;
    loadUsers(projectScope).then((options) => {
      if (!cancelled) setUserOptions(options);
    });
    return () => {
      cancelled = true;
    };
  }, [projectScope, loadUsers]);

  const buildParams = () => {
    const params: Record<string, string | number> = {};
    if (selectedProjects.length > 0) {
      params.project_id = selectedProjects.map((option) => option.value).join(',');
    }
    if (selectedUser) params.user_id = Number(selectedUser.value);
    if (selectedDepartment) params.department_id = Number(selectedDepartment.value);
    if (selectedActivity) params.activity_id = Number(selectedActivity.value);
    if (selectedRole) params.role_id = Number(selectedRole.value);
    if (filters.site_id) params.site_id = filters.site_id;
    if (filters.search) params.search = filters.search;
    if (filters.stage_date_from) params.stage_date_from = filters.stage_date_from;
    if (filters.stage_date_to) params.stage_date_to = filters.stage_date_to;
    params.completion_status =
      filters.completion_status || DEFAULT_SUMMARY_FILTERS.completion_status;
    return params;
  };

  // Both buttons read the filters as typed, so both check the range first.
  const stageDateRangeError = () =>
    dateRangeError(filters.stage_date_from, filters.stage_date_to, 'Stage Date');
  const stageDateBounds = dateRangeInputBounds(filters.stage_date_from, filters.stage_date_to);

  const fetchSummary = async () => {
    const dateError = stageDateRangeError();
    if (dateError) {
      toast.error(dateError);
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/survey-workflow/summary', {
        params: buildParams(),
      });
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to fetch the stage-wise counts');
        return;
      }
      const payload = response.data.data || {};
      setSummary({
        projects: payload.projects || [],
        rows: payload.rows || [],
        totals: payload.totals || { all: 0, counts: {} },
      });
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to fetch the stage-wise counts');
    } finally {
      setLoading(false);
    }
  };

  // The workbook is built server-side from the same query as the table, so the
  // export cannot drift from what is on screen.
  const handleExport = async () => {
    const dateError = stageDateRangeError();
    if (dateError) {
      toast.error(dateError);
      return;
    }
    try {
      setExporting(true);
      const response = await axiosInstance.get('/reports/survey-workflow/summary/export', {
        params: buildParams(),
        responseType: 'blob',
      });

      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match ? decodeURIComponent(match[1]) : 'survey-workflow-stagewise.xlsx';

      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (error: any) {
      let message = 'Failed to export the stage-wise counts';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.message || message;
        } catch {
          /* not JSON — keep the generic message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setSelectedProjects([]);
    setSelectedUser(null);
    setSelectedDepartment(null);
    setSelectedActivity(null);
    setSelectedRole(null);
    setFilters(DEFAULT_SUMMARY_FILTERS);
  };

  const handleFilterChange = (key: keyof SummaryFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <div className="bg-white shadow-sm rounded border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Summary View Filters</h2>
          <p className="text-sm text-gray-600">
            Every filter is optional, and they are the same ones the List view offers — minus
            the workflow stage, which the matrix already has a row for. Leave Project empty to
            get a column for every project that has sites; pick projects to limit the columns to
            those. Department (Project Group) narrows the sites to the projects that department
            owns, and the date window matches sites that entered their current stage inside it.
          </p>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Project (your projects only)</span>
            <SearchableSelect
              placeholder="All my projects"
              value={selectedProjects}
              options={projectOptions}
              isMulti
              showSelectAllVisible
              onChange={(options: SelectOption[] | SelectOption | null) => {
                const next = Array.isArray(options) ? options : options ? [options] : [];
                setSelectedProjects(next);
                setSelectedUser(null);
              }}
              className="w-full"
            />
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Engineer</span>
            <SelectWithSearch
              placeholder="All engineers"
              value={selectedUser}
              options={userOptions}
              onChange={(option) => setSelectedUser(option)}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">
              Department / Project Group
            </span>
            <SelectWithSearch
              placeholder="All departments"
              value={selectedDepartment}
              options={departmentOptions}
              onChange={(option) => setSelectedDepartment(option)}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Activity</span>
            <SelectWithSearch
              placeholder="All activities"
              value={selectedActivity}
              options={activityOptions}
              onChange={(option) => setSelectedActivity(option)}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Role</span>
            <SelectWithSearch
              placeholder="All roles"
              value={selectedRole}
              options={roleOptions}
              onChange={(option) => setSelectedRole(option)}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Stage Date From</span>
            <input
              type="date"
              value={filters.stage_date_from}
              onChange={(event) => handleFilterChange('stage_date_from', event.target.value)}
              {...stageDateBounds.from}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Stage Date To</span>
            <input
              type="date"
              value={filters.stage_date_to}
              onChange={(event) => handleFilterChange('stage_date_to', event.target.value)}
              {...stageDateBounds.to}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <div className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">
              Work Completion Status
            </span>
            <CompletionStatusFilter
              value={filters.completion_status}
              onChange={(value) => handleFilterChange('completion_status', value)}
            />
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Site ID</span>
            <input
              type="text"
              value={filters.site_id}
              onChange={(event) => handleFilterChange('site_id', event.target.value)}
              placeholder="SIT"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Search</span>
            <input
              type="text"
              value={filters.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
              placeholder="Site ID, name or reference"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </label>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={handleExport}
            disabled={!summary || exporting}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
          >
            {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
            {exporting ? 'Preparing...' : 'Export Excel'}
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={fetchSummary}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {!summary && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <FaTable className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">Apply filters to load the stage-wise counts.</p>
            <p className="text-xs text-gray-400 mt-1">Every filter is optional.</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky top-0 left-0 z-20 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                    Survey Workflow Stage
                  </th>
                  <th className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-right text-xs font-semibold text-gray-900 whitespace-nowrap">
                    All
                  </th>
                  {(summary?.projects || []).map((project) => (
                    <th
                      key={project.id}
                      title={project.project_name}
                      className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-right text-xs font-semibold text-gray-600 whitespace-nowrap"
                    >
                      {project.project_short_name || project.project_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={(summary?.projects.length || 0) + 2}
                      className="px-4 py-10 text-center text-sm text-gray-600"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : (summary?.projects.length || 0) === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-4 py-10 text-center text-sm text-gray-600">
                      No sites found for these filters.
                    </td>
                  </tr>
                ) : (
                  (summary?.rows || []).map((row) => (
                    <tr key={row.stage} className="hover:bg-gray-50">
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {row.sequence}. {row.stage_label}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 whitespace-nowrap">
                        {row.all}
                      </td>
                      {(summary?.projects || []).map((project) => (
                        <td
                          key={project.id}
                          className="px-4 py-3 text-sm text-right text-gray-700 whitespace-nowrap"
                        >
                          {row.counts[String(project.id)] ?? 0}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && (summary?.projects.length || 0) > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 border-t border-gray-200">
                    <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Total
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 whitespace-nowrap">
                      {summary?.totals.all ?? 0}
                    </td>
                    {(summary?.projects || []).map((project) => (
                      <td
                        key={project.id}
                        className="px-4 py-3 text-sm text-right font-semibold text-gray-900 whitespace-nowrap"
                      >
                        {summary?.totals.counts[String(project.id)] ?? 0}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default SurveyWorkflowSummary;
